import type { GetOrganizationsInGeoAreaTagsEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { isEmptyOrNull } from '@/shared/utils/string';

type TagValue = GetOrganizationsInGeoAreaTagsEnum;

type Params = {
  selectedTags: TagValue[];
  selectedGeoAreaIds: string[];
  geoAreaNameById: Map<string, string>;
};

/**
 * Builds the display labels for the geo-area and tag filter chips:
 * - empty selection → placeholder
 * - single selection → the resolved name
 * - multiple (or unresolved) → localized "n selected" count
 */
export const useOrganizationFilterLabels = ({
  selectedTags,
  selectedGeoAreaIds,
  geoAreaNameById
}: Params) => {
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-area.filters'
  });
  const { t: tTags } = useTranslation(LocalizationNameSpace.ORGANIZATION, {
    keyPrefix: 'organization.tagLabels'
  });

  const geoAreaLabel = useMemo(() => {
    if (selectedGeoAreaIds.length === 0) return t('geoAreaPlaceholder');
    const names = selectedGeoAreaIds
      .map((id) => geoAreaNameById.get(id))
      .filter((name): name is string => !isEmptyOrNull(name));
    if (names.length === 0) {
      return t('geoAreaSelectedCount', { count: selectedGeoAreaIds.length });
    }
    if (names.length === 1) return names[0];
    return t('geoAreaSelectedCount', { count: names.length });
  }, [geoAreaNameById, selectedGeoAreaIds, t]);

  const tagsLabel = useMemo(() => {
    if (selectedTags.length === 0) return t('tagsPlaceholder');
    if (selectedTags.length === 1) return tTags(selectedTags[0]);
    return t('tagsSelectedCount', { count: selectedTags.length });
  }, [selectedTags, t, tTags]);

  return { geoAreaLabel, tagsLabel };
};
